import type { VisualPayload, RaidBoss, MaxBattle } from "../../types";
import { isSafeUrl } from "../../lib/safeUrl";
import CardShell from "./_CardShell";
import PokemonSprite from "../PokemonSprite";
import { TYPE_CHIP_CLASSES } from "./_seasonShared";

interface Props { payload: VisualPayload }

// Per-tier accent colors so the visual hierarchy reads at a glance.
// Higher tiers get warmer/brighter colors; lower tiers stay muted.
const TIER_ACCENT: Record<string, { border: string; label: string; pill: string }> = {
  "5-Star Raids": {
    border: "border-amber-500/30",
    label: "text-amber-300",
    pill: "bg-amber-500/15 border-amber-500/25 text-amber-300",
  },
  "Mega Raids": {
    border: "border-violet-500/30",
    label: "text-violet-300",
    pill: "bg-violet-500/15 border-violet-500/25 text-violet-300",
  },
  "Shadow Raids": {
    border: "border-purple-500/30",
    label: "text-purple-300",
    pill: "bg-purple-500/15 border-purple-500/25 text-purple-300",
  },
  "Elite Raids": {
    border: "border-pink-500/30",
    label: "text-pink-300",
    pill: "bg-pink-500/15 border-pink-500/25 text-pink-300",
  },
  "3-Star Raids": {
    border: "border-blue-500/20",
    label: "text-blue-300",
    pill: "bg-blue-500/10 border-blue-500/20 text-blue-300",
  },
  "1-Star Raids": {
    border: "border-white/15",
    label: "text-white/55",
    pill: "bg-white/5 border-white/15 text-white/60",
  },
  "Max Battles": {
    border: "border-emerald-500/30",
    label: "text-emerald-300",
    pill: "bg-emerald-500/15 border-emerald-500/25 text-emerald-300",
  },
};

const FALLBACK_TIER_ACCENT = {
  border: "border-white/15",
  label: "text-white/55",
  pill: "bg-white/5 border-white/15 text-white/60",
};

/** ShinySparkle — small star icon next to shiny-capable bosses. */
function ShinySparkle() {
  return (
    <span
      aria-label="shiny available"
      title="Shiny available"
      className="text-yellow-300 text-[11px]"
    >
      ✦
    </span>
  );
}

function TypeChips({ types }: { types: string[] }) {
  if (!types || types.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {types.map((t) => (
        <span
          key={t}
          className={`text-[10px] px-1.5 py-0 rounded border capitalize ${
            TYPE_CHIP_CLASSES[t.toLowerCase()] ?? "bg-white/10 text-white/70 border-white/15"
          }`}
        >
          {t}
        </span>
      ))}
    </span>
  );
}

function CpRange({ b }: { b: RaidBoss }) {
  if (b.cp_normal_min == null || b.cp_normal_max == null) return null;
  return (
    <span className="text-[10px] text-white/40 tabular-nums shrink-0">
      {b.cp_normal_min.toLocaleString()}–{b.cp_normal_max.toLocaleString()}
    </span>
  );
}

// --- Max Battles helpers -------------------------------------------------
//
// Max Battles (Dynamax + Gigantamax) carry a tonally different visual
// because they're scheduled events rather than continuous rotation —
// each row shows the event window + an "ACTIVE NOW" badge when the
// window covers today.  Per-kind accents: Dynamax leans red (Max Move
// red is the Pokémon Sword/Shield branding), Gigantamax leans crimson.
const MB_ACCENT: Record<MaxBattle["kind"], { border: string; label: string; badge: string }> = {
  dynamax: {
    border: "border-red-500/30",
    label: "text-red-300",
    badge: "bg-red-500/15 border-red-500/25 text-red-300",
  },
  gigantamax: {
    border: "border-rose-600/40",
    label: "text-rose-300",
    badge: "bg-rose-600/20 border-rose-600/35 text-rose-200",
  },
};

function _fmtMbDate(iso?: string | null): string {
  if (!iso) return "TBD";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function MaxBattleRow({ mb }: { mb: MaxBattle }) {
  const accent = MB_ACCENT[mb.kind] ?? MB_ACCENT.dynamax;
  // Strip the trailing " during Max Monday" suffix to make the
  // Pokémon name read cleanly on its own line.
  const displayName = (mb.name ?? "").replace(/\s+during Max Monday$/i, "");
  return (
    <li className={`flex flex-col gap-1 text-xs border-l-2 pl-2.5 py-1 ${accent.border}`}>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-white/85 font-medium">{displayName}</span>
        {mb.is_active && (
          <span className={`text-[9px] uppercase tracking-wider px-1 py-px rounded border ${accent.badge}`}>
            Live now
          </span>
        )}
        <span className={`text-[9px] uppercase tracking-wider px-1 py-px rounded border ${accent.badge}`}>
          {mb.kind === "gigantamax" ? "Gigantamax" : "Dynamax"}
        </span>
      </div>
      <p className="text-[10px] text-white/45">
        {_fmtMbDate(mb.starts_at)}
        {mb.ends_at && mb.ends_at.slice(0, 10) !== (mb.starts_at ?? "").slice(0, 10) && (
          <> – {_fmtMbDate(mb.ends_at)}</>
        )}
        {isSafeUrl(mb.link) && (
          <>
            {" · "}
            <a
              href={mb.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-red-400/70 hover:text-red-300 transition-colors underline-offset-2 hover:underline"
            >
              details
            </a>
          </>
        )}
      </p>
    </li>
  );
}

/**
 * "Tell me about today's raids" — current rotation grouped by tier.
 * Higher tiers (5-Star, Mega) get more visual weight via the per-tier
 * accent palette.  Type-colored chips match the rest of the app
 * (TYPE_CHIP_CLASSES is the shared map from _seasonShared.tsx so
 * Pokemon GO type colors are consistent across cards).
 *
 * Phase 3.
 */
export default function RaidBossesCard({ payload }: Props) {
  const sections = payload.sections ?? [];
  const summarySec = sections.find((s) => s.section === "raid_summary");
  const byTierSec = sections.find((s) => s.section === "raid_bosses_by_tier");
  const maxBattlesSec = sections.find((s) => s.section === "max_battles");
  const byTier = (byTierSec?.by_tier as Record<string, RaidBoss[]> | undefined) ?? {};
  const tierCounts = (summarySec?.tier_counts as Record<string, number> | undefined) ?? {};
  const totalBosses = (summarySec?.total_bosses as number | undefined) ?? 0;
  const maxBattles = (maxBattlesSec?.items as MaxBattle[] | undefined) ?? [];
  const maxBattleActiveCount = (summarySec?.max_battle_active_count as number | undefined) ?? 0;

  const tierEntries = Object.entries(byTier);

  // Empty only when BOTH standard rotation AND Max Battles are empty.
  if (tierEntries.length === 0 && maxBattles.length === 0) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined} accent="border-amber-500/20">
        <p className="text-sm text-white/55 py-2">No current raid boss data available.</p>
      </CardShell>
    );
  }

  return (
    <CardShell
      title={payload.title}
      source={payload.data_source}
      timestamp={payload.data_timestamp}
      sourceUrl={payload.source_url}
      shareUrl={payload.share_url}
      snapshotPayload={payload.snapshot_eligible ? payload : undefined}
      accent="border-amber-500/30"
    >
      {/* Top strip: total count + per-tier chips for fast scan */}
      <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-white/[0.06]">
        <span className="text-sm text-white/70">
          {totalBosses} bosses in rotation
        </span>
        <span className="text-white/20" aria-hidden="true">·</span>
        {Object.entries(tierCounts).map(([tier, n]) => {
          const accent = TIER_ACCENT[tier] ?? FALLBACK_TIER_ACCENT;
          const shortLabel = tier.replace(" Raids", "");
          return (
            <span
              key={tier}
              className={`text-[10px] px-1.5 py-0.5 rounded border ${accent.pill}`}
            >
              {shortLabel}: {n}
            </span>
          );
        })}
        {maxBattles.length > 0 && (
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded border ${
              maxBattleActiveCount > 0
                ? MB_ACCENT.dynamax.badge
                : "bg-white/5 border-white/15 text-white/60"
            }`}
          >
            Max Battles: {maxBattles.length}
            {maxBattleActiveCount > 0 && <> ({maxBattleActiveCount} live)</>}
          </span>
        )}
      </div>

      {/* Per-tier sections */}
      <div className="space-y-4">
        {tierEntries.map(([tier, bosses]) => {
          const accent = TIER_ACCENT[tier] ?? FALLBACK_TIER_ACCENT;
          return (
            <section key={tier}>
              <h4 className={`text-[11px] font-semibold uppercase tracking-widest mb-2 ${accent.label}`}>
                {tier} ({bosses.length})
              </h4>
              <ul className="space-y-1.5">
                {bosses.map((b, i) => (
                  <li
                    key={i}
                    className={`flex sm:items-baseline gap-2 text-xs border-l-2 pl-2.5 py-0.5 ${accent.border}`}
                  >
                    {/* Sprite stays at the left edge on every viewport so the
                        bosses scan as a tidy column.  PokemonSprite returns
                        null when sprites are disabled — layout collapses
                        gracefully without any extra width. */}
                    <PokemonSprite src={b.image ?? undefined} alt={b.name} size="sm" />
                    <span className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 flex-1 min-w-0">
                      <span className="flex items-center gap-1.5 sm:min-w-[8rem] shrink-0">
                        <span className="text-white/85 font-medium">{b.name}</span>
                        {b.can_be_shiny && <ShinySparkle />}
                      </span>
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:flex-1">
                        <TypeChips types={b.types ?? []} />
                        <CpRange b={b} />
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        {/* Max Battles section — distinct from tiered raids because
            these are time-windowed events at Power Spots, not the
            continuous rotation at Gyms.  We always render this when
            data is present so users asking "what's in raids" don't
            miss Dynamax / Gigantamax content. */}
        {maxBattles.length > 0 && (
          <section>
            <h4 className={`text-[11px] font-semibold uppercase tracking-widest mb-2 ${MB_ACCENT.dynamax.label}`}>
              Max Battles ({maxBattles.length})
            </h4>
            <ul className="space-y-2">
              {maxBattles.map((mb, i) => (
                <MaxBattleRow key={i} mb={mb} />
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* Attribution footer (per ScrapedDuck licence — credit both
          LeekDuck and ScrapedDuck in user-facing surfaces). */}
      <div className="mt-4 pt-3 border-t border-white/[0.06]">
        <p className="text-[11px] text-white/35">
          Raid data from{" "}
          <a
            href="https://leekduck.com/boss/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-400/70 hover:text-amber-300 transition-colors"
          >
            LeekDuck
          </a>{" "}
          via{" "}
          <a
            href="https://github.com/bigfoott/ScrapedDuck"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-400/70 hover:text-amber-300 transition-colors"
          >
            ScrapedDuck
          </a>
        </p>
      </div>
    </CardShell>
  );
}
